import type { FaqItem } from '../components/Faq';
import { MONTHLY_PRICE, SETUP_PRICE } from '../components/WebsiteOffer/offerData';

export type WebsiteLocalSlug = 'annemasse' | 'saint-julien-en-genevois' | 'pays-de-gex' | 'annecy';

export interface WebsiteLocalPage {
  slug: WebsiteLocalSlug;
  path: string;
  /** Nom court utilisé dans les liens ("Annemasse"). */
  shortName: string;
  /** Zone desservie pour le JSON-LD. */
  areaName: string;
  metaTitle: string;
  metaDescription: string;
  h1: string;
  /** Texte d'intro (2 paragraphes), spécifique à la zone. */
  intro: readonly string[];
  /** Questions locales, affichées avant les questions générales. */
  faqs: FaqItem[];
  nearbyTowns: readonly string[];
}

const PRICE_SENTENCE = `Pas d'acompte : vous réglez ${SETUP_PRICE} € à la mise en ligne, puis ${MONTHLY_PRICE} € par mois, avec modifications de contenu sous 7 jours.`;

export const WEBSITE_LOCAL_PAGES: readonly WebsiteLocalPage[] = [
  {
    slug: 'annemasse',
    path: '/creation-site-internet-annemasse',
    shortName: 'Annemasse',
    areaName: 'Annemasse',
    metaTitle: `Création site internet Annemasse dès ${SETUP_PRICE} € | SOS IT`,
    metaDescription: `Création de site internet à Annemasse (74100) : ${SETUP_PRICE} € à la mise en ligne puis ${MONTHLY_PRICE} €/mois, sans acompte. Rendez-vous sur place ou en visio.`,
    h1: 'Création de site internet à Annemasse',
    intro: [
      "Annemasse est une ville d'activité aux portes de Genève : commerces de centre-ville, artisans, professions libérales et indépendants de tous secteurs y cohabitent. Pour beaucoup, la première étape d'un client est la même : une recherche sur Google, puis un coup d'œil au site internet avant de passer la porte ou de décrocher le téléphone.",
      `Basés à Viry, nous sommes à environ 25 minutes d'Annemasse. Nous pouvons venir vous rencontrer sur place pour un premier échange, ou faire le point en visio si vous préférez gagner du temps. Nous présentons votre activité, vos horaires et votre zone d'intervention, et nous suivons votre fiche Google pour que votre site soit trouvé quand on cherche près de chez vous. Le nom de domaine et l'hébergement sont à votre nom, nous nous occupons de toute la configuration. ${PRICE_SENTENCE}`,
    ],
    faqs: [
      {
        question: 'Vous déplacez-vous à Annemasse pour le premier rendez-vous ?',
        answer:
          'Oui. Annemasse se trouve à environ 25 minutes de Viry : nous pouvons venir à votre commerce ou à votre bureau pour un premier échange de 15 minutes. Si vous préférez, nous le faisons en visio ou par WhatsApp.',
      },
      {
        question: 'Je suis commerçant ou artisan à Annemasse : quelles pages prévoir pour mon site ?',
        answer:
          "En général : accueil, présentation de vos services ou produits, galerie de réalisations, informations pratiques (adresse, horaires, plan d'accès) et contact. L'offre comprend jusqu'à 10 pages, nous définissons ensemble la structure qui vous correspond.",
      },
    ],
    nearbyTowns: ['Ambilly', 'Gaillard', 'Ville-la-Grand', 'Cranves-Sales', 'Vétraz-Monthoux', 'Étrembières', 'Machilly', 'Bonne'],
  },
  {
    slug: 'saint-julien-en-genevois',
    path: '/creation-site-internet-saint-julien-en-genevois',
    shortName: 'Saint-Julien-en-Genevois',
    areaName: 'Saint-Julien-en-Genevois',
    metaTitle: `Création site internet Saint-Julien-en-Genevois dès ${SETUP_PRICE} €`,
    metaDescription: `Création de site internet à Saint-Julien-en-Genevois (74160) : ${SETUP_PRICE} € à la mise en ligne puis ${MONTHLY_PRICE} €/mois, sans acompte. Basés à 10 min, à Viry.`,
    h1: 'Création de site internet à Saint-Julien-en-Genevois',
    intro: [
      "Saint-Julien-en-Genevois est une commune frontalière où se côtoient commerces de proximité, artisans, indépendants et professionnels qui travaillent avec une clientèle de l'autre côté de la frontière. Un site internet clair, en français, permet de présenter votre activité aussi bien aux habitants du Genevois qu'aux clients qui vous trouvent depuis Genève.",
      `Notre entreprise est basée à Viry, à environ 10 minutes de Saint-Julien : c'est notre voisine directe. Nous nous déplaçons volontiers pour un rendez-vous dans votre local ou à votre domicile, ou nous échangeons en visio selon votre emploi du temps. Nous rédigeons une première version de vos textes, vous la relisez et la validez, puis nous mettons en ligne. Votre nom de domaine et votre hébergement restent à votre nom. ${PRICE_SENTENCE}`,
    ],
    faqs: [
      {
        question: 'Vous déplacez-vous à Saint-Julien-en-Genevois ?',
        answer:
          "Oui, c'est à environ 10 minutes de Viry, là où nous sommes basés. Nous pouvons nous rencontrer dans votre commerce, votre atelier ou chez vous pour le premier rendez-vous, puis échanger par WhatsApp ou en visio pour la suite.",
      },
      {
        question: 'Ma clientèle est en partie suisse : puis-je le mentionner sur mon site ?',
        answer:
          "Oui, vous pouvez indiquer sur votre site que vous intervenez auprès d'une clientèle du Genevois et de Genève, ainsi que vos zones d'intervention. Le site est rédigé en français ; pour toute question fiscale ou juridique liée à votre activité transfrontalière, rapprochez-vous de votre comptable ou d'un professionnel du droit.",
      },
    ],
    nearbyTowns: ['Viry', 'Valleiry', 'Archamps', 'Neydens', 'Beaumont', 'Collonges-sous-Salève', 'Feigères', 'Vulbens'],
  },
  {
    slug: 'pays-de-gex',
    path: '/creation-site-internet-pays-de-gex',
    shortName: 'Pays de Gex',
    areaName: 'Pays de Gex',
    metaTitle: `Création site internet Pays de Gex dès ${SETUP_PRICE} € | SOS IT`,
    metaDescription: `Création de site internet dans le Pays de Gex (Gex, Saint-Genis-Pouilly, Ferney-Voltaire, Divonne) : ${SETUP_PRICE} € puis ${MONTHLY_PRICE} €/mois, sans acompte.`,
    h1: 'Création de site internet dans le Pays de Gex',
    intro: [
      "Le Pays de Gex, dans l'Ain, regroupe des communes très différentes : Gex, Saint-Genis-Pouilly, Ferney-Voltaire, Divonne-les-Bains et leurs voisines. On y trouve des artisans, des commerces, des professions libérales et de nombreux indépendants, dont certains travaillent avec une clientèle suisse. Un site internet en français, clair sur vos services et vos zones d'intervention, vous aide à être trouvé localement.",
      `Basés à Viry, en Haute-Savoie, nous intervenons aussi dans l'Ain : nous nous déplaçons dans le Pays de Gex pour un premier rendez-vous sur place, ou nous travaillons en visio si c'est plus pratique pour vous. Nous nous occupons de la création, de la configuration du nom de domaine et de l'hébergement, qui restent à votre nom, puis des mises à jour. ${PRICE_SENTENCE}`,
    ],
    faqs: [
      {
        question: 'Mon activité est en Suisse mais je suis domicilié dans le Pays de Gex, est-ce possible ?',
        answer:
          'Oui, nous créons des sites pour des indépendants dont une partie de la clientèle est en Suisse. Votre site est rédigé en français et peut mentionner les zones où vous intervenez. Nous ne donnons pas de conseil fiscal ou juridique : pour votre situation transfrontalière, adressez-vous à votre comptable.',
      },
      {
        question: 'Vous déplacez-vous à Gex, Ferney-Voltaire, Saint-Genis-Pouilly ou Divonne-les-Bains ?',
        answer:
          "Oui, nous pouvons nous déplacer dans le Pays de Gex pour un premier rendez-vous. Selon la commune et notre planning, nous convenons ensemble d'un créneau ; la suite du projet peut se faire en visio ou par WhatsApp.",
      },
      {
        question: 'Un seul site peut-il présenter plusieurs communes du Pays de Gex ?',
        answer:
          "Oui. Vous pouvez préciser sur une page les communes où vous intervenez, tout en gardant un site simple et lisible. L'offre comprend jusqu'à 10 pages.",
      },
    ],
    nearbyTowns: ['Gex', 'Saint-Genis-Pouilly', 'Ferney-Voltaire', 'Divonne-les-Bains', 'Prévessin-Moëns', 'Cessy', 'Ornex', 'Segny', 'Thoiry'],
  },
  {
    slug: 'annecy',
    path: '/creation-site-internet-annecy',
    shortName: 'Annecy',
    areaName: 'Annecy',
    metaTitle: `Création site internet Annecy dès ${SETUP_PRICE} € | SOS IT`,
    metaDescription: `Création de site internet à Annecy (74000) : ${SETUP_PRICE} € à la mise en ligne puis ${MONTHLY_PRICE} €/mois, sans acompte. Rendez-vous sur place ou en visio.`,
    h1: 'Création de site internet à Annecy',
    intro: [
      'Annecy et son agglomération réunissent une activité variée : commerces de centre-ville, restauration et hébergement, activités liées au tourisme, artisans et indépendants. Dans un secteur où les clients comparent avant de choisir, un site internet soigné, lisible sur mobile et bien référencé fait la différence au moment de la recherche.',
      `Nous sommes basés à Viry. Pour Annecy, nous organisons le plus souvent le premier échange en visio ou par WhatsApp, et nous pouvons prévoir un rendez-vous sur place quand le projet le justifie. Nous réalisons un site simple et efficace : présentation de votre activité, photos, informations pratiques, formulaire de contact, intégration de votre fiche Google. Nom de domaine et hébergement sont à votre nom. ${PRICE_SENTENCE}`,
    ],
    faqs: [
      {
        question: 'Vous êtes basés à Viry : pouvez-vous vraiment travailler avec un commerce à Annecy ?',
        answer:
          'Oui. Tout le projet peut se faire à distance (visio, WhatsApp, email), et un rendez-vous sur place reste possible pour un premier échange ou pour réaliser des photos avec vous. Nous vous proposons la formule qui vous convient.',
      },
      {
        question: "J'ai une activité saisonnière ou touristique à Annecy : mon site peut-il évoluer ?",
        answer:
          "Oui. Les modifications de contenu (horaires, tarifs, photos, périodes d'ouverture) sont incluses dans l'abonnement mensuel et réalisées sous 7 jours.",
      },
    ],
    nearbyTowns: ['Épagny Metz-Tessy', 'Poisy', 'Argonay', 'Chavanod', 'Sévrier', 'Saint-Jorioz', 'Veyrier-du-Lac', 'Menthon-Saint-Bernard'],
  },
];

export const getWebsiteLocalPage = (slug: WebsiteLocalSlug): WebsiteLocalPage => {
  const page = WEBSITE_LOCAL_PAGES.find((entry) => entry.slug === slug);
  if (!page) throw new Error(`Page locale site internet inconnue : ${slug}`);
  return page;
};

/** "à Annemasse" / "dans le Pays de Gex". */
export const localPreposition = (page: Pick<WebsiteLocalPage, 'slug' | 'shortName'>): string =>
  page.slug === 'pays-de-gex' ? `dans le ${page.shortName}` : `à ${page.shortName}`;
